import { Box, Skeleton, Toolbar } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import compact from "lodash/compact";
import first from "lodash/first";
import isEmpty from "lodash/isEmpty";
import { FC, useCallback, useEffect, useMemo } from "react";
import { useLocation, useMatch } from "react-router-dom";
import useCitiesQuery from "~@/api/queries/useCitiesQuery";
import useTournamentsByCityQuery from "~@/api/queries/useTournamentsByCityQuery";
import { useHomeContext } from "~@/pages/home-page/context/home-context";
import CitySelector from "./CitySelector";
import TournamentSelector from "./TournamentSelector";

const MobileHeader: FC = () => {
  const match1 = useMatch("/players");
  const match2 = useMatch("/teams");

  const _location = useLocation();

  const { cityId, setCityId, tournamentId, setTournamentId } = useHomeContext();

  const { data: citiesData, isLoading: areCitiesLoading } = useCitiesQuery();

  const {
    data: tournamentsData,
    isLoading: areTournamentsLoading,
    isPending: areTournamentsPending,
  } = useTournamentsByCityQuery(cityId);

  const cities = useMemo(() => {
    const allCities = compact(citiesData);
    const isHomePage = _location.pathname === "/";
    
    if (isHomePage) {
      return allCities.filter((city) => {
        const cityTitle = city.title?.toLowerCase().trim();
        return cityTitle === "алматы" || cityTitle === "астана";
      });
    }
    
    return allCities;
  }, [citiesData, _location.pathname]);

  const tournaments = useMemo(() => {
    return compact(tournamentsData);
  }, [tournamentsData]);

  const onCityChange = useCallback(
    (value: number) => {
      setCityId(value);
      setTournamentId(undefined);
    },
    [setCityId, setTournamentId]
  );

  const onTournamentChange = useCallback(
    (value: number) => {
      setTournamentId(value);
    },
    [setTournamentId]
  );

  const setDefaultCity = useCallback(() => {
    if (isEmpty(cities)) return;

    const defaultId = first(cities)!.id;

    if (typeof defaultId === "number") {
      setCityId(defaultId);
    }
  }, [cities, setCityId]);

  const setDefaultTournament = useCallback(() => {
    if (isEmpty(tournaments)) return;

    const defaultId = first(tournaments)!.id;
    if (typeof defaultId === "number") {
      setTournamentId(defaultId);
    }
  }, [tournaments, setTournamentId]);

  useEffect(() => {
    if (typeof cityId === "undefined") {
      setDefaultCity();
    }
  }, [setDefaultCity, cityId]);

  useEffect(() => {
    if (typeof tournamentId === "undefined") {
      setDefaultTournament();
    }
  }, [setDefaultTournament, tournamentId, cityId, tournaments]);

  useEffect(() => {
    const isHomePage = _location.pathname === "/";
    
    if (isHomePage && typeof cityId !== "undefined" && !isEmpty(cities)) {
      const isCityAvailable = cities.some((city) => city.id === cityId);
      
      if (!isCityAvailable) {
        const defaultCityId = first(cities)?.id;
        if (typeof defaultCityId === "number") {
          setCityId(defaultCityId);
          setTournamentId(undefined);
        }
      }
    }
  }, [_location.pathname, cityId, cities, setCityId, setTournamentId]);

  return (
    <AppBar
      position="sticky"
      color="transparent"
      elevation={0}
      sx={{
        borderBottom: "1px solid #e5e5e5",
        bgcolor: "background.paper",
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          flexDirection: "column",
          px: 2,
          pt: 3,
          pb: !match1 && !match2 ? 1.5 : 2,
          gap: 2,
          height: "100%",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: match1 || match2 ? "1fr" : "1fr 1fr",
            alignItems: "center",
            gap: 1,
            width: "100%",
          }}
        >
          {areCitiesLoading || typeof cityId === "undefined" ? (
            <Skeleton variant="rounded" width={"100%"} height={32} />
          ) : (
            <CitySelector
              cities={cities}
              cityId={cityId}
              onCityChange={onCityChange}
            />
          )}
          {!match1 &&
            !match2 &&
            (areTournamentsPending ||
            areTournamentsLoading ||
            typeof tournamentId === "undefined" ? (
              <Skeleton variant="rounded" width={"100%"} height={32} />
            ) : (
              <TournamentSelector
                tournaments={tournaments}
                tournamentId={tournamentId}
                onTournamentChange={onTournamentChange}
                backgroundColor="gray"
              />
            ))}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default MobileHeader;
